import React, { useState } from "react";
import { Info, XCircle } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { cn } from "../../utils/classMerger";
import type { SocialConnectionMetrics } from "./types";

interface SocialConnectionMetricCardsProps {
    metrics: SocialConnectionMetrics;
    isMobile: boolean;
}

const infoMessages: Record<keyof SocialConnectionMetrics, string> = {
    totalUsers:
        "The total number of users who hold an Ayrshare profile, whether or not they currently have any account connected.",
    connectedAccounts:
        "The total number of social accounts currently connected across all users.",
    usersWithDisconnectedAccounts:
        "The number of users who currently have at least one social account disconnected from Ayrshare.",
    disconnectedAccounts:
        "The total number of individual social accounts that are currently disconnected across all users.",
    usersWithSocialPausePosts:
        "The number of users who have scheduled posts stranded on Social Pause, waiting for a platform to be reconnected.",
    socialPausePosts:
        "The total number of scheduled posts currently held on Social Pause across all users.",
};

const SocialConnectionMetricCards: React.FC<
    SocialConnectionMetricCardsProps
> = ({ metrics, isMobile }) => {
    const [infoMetric, setInfoMetric] = useState<
        keyof SocialConnectionMetrics | null
    >(null);

    const cards: {
        key: keyof SocialConnectionMetrics;
        title: string;
        caption: string;
    }[] = [
        {
            key: "totalUsers",
            title: "Users Tracked",
            caption: "With an Ayrshare profile",
        },
        {
            key: "connectedAccounts",
            title: "Connected Accounts",
            caption: "Currently connected",
        },
        {
            key: "usersWithDisconnectedAccounts",
            title: "Users Affected",
            caption: "With a disconnect",
        },
        {
            key: "disconnectedAccounts",
            title: "Disconnected Accounts",
            caption: "Currently disconnected",
        },
        {
            key: "usersWithSocialPausePosts",
            title: "Users With Paused Posts",
            caption: "With paused posts",
        },
        {
            key: "socialPausePosts",
            title: "Posts On Social Pause",
            caption: "Awaiting a reconnect",
        },
    ];

    return (
        <>
            {/* Static class strings — Tailwind cannot see `grid-cols-${n}` templates. */}
            <div
                className={cn(
                    "grid gap-4",
                    isMobile ? "grid-cols-2" : "grid-cols-6"
                )}
            >
                {cards.map(({ key, title, caption }) => (
                    <Card key={key} className="mt-0 flex flex-col">
                        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 p-4 pb-1">
                            <CardTitle className="text-left text-sm font-semibold">
                                {title}
                            </CardTitle>
                            <Info
                                className="h-4 w-4 shrink-0 text-secondary cursor-pointer"
                                onClick={() => setInfoMetric(key)}
                            />
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                            <div className="text-2xl font-bold text-primary">
                                {metrics[key].toLocaleString()}
                            </div>
                            {/* gray-500, not text-muted-foreground: --muted-foreground is
                                undefined app-wide, so that class renders near-black. */}
                            <p className="text-xs text-gray-500">{caption}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {infoMetric && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full relative">
                        <button
                            className="absolute top-2 right-2"
                            onClick={() => setInfoMetric(null)}
                        >
                            <XCircle className="h-5 w-5 text-red-500" />
                        </button>
                        <h2 className="text-lg font-semibold mb-2 text-primary">
                            {infoMetric
                                .replace(/([A-Z])/g, " $1")
                                .trim()
                                .replace(/^./, (char) => char.toUpperCase())}
                        </h2>
                        <p className="text-sm text-gray-700">
                            {infoMessages[infoMetric]}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default SocialConnectionMetricCards;
